import { useEffect, useState } from "react";
import { Eye, Users, MousePointerClick, Activity } from "lucide-react";
import { ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";
import { api } from "@/lib/api";

const RANGES = [[1, "Today"], [7, "7 days"], [30, "30 days"], [90, "90 days"]];

function Kpi({ icon: Icon, label, value, testId }) {
  return (
    <div className="bg-white rounded-2xl border border-[#0B2A4A]/10 p-5" data-testid={testId}>
      <p className="flex items-center gap-2 text-xs uppercase tracking-wider text-slate-500"><Icon className="h-4 w-4 text-[#1A9AA8]" /> {label}</p>
      <p className="mt-2 text-3xl font-bold text-[#0B2A4A]">{value.toLocaleString()}</p>
    </div>
  );
}

function TopList({ title, rows, testId, unit = "views" }) {
  const max = Math.max(1, ...rows.map((r) => r.count));
  return (
    <div className="bg-white rounded-2xl border border-[#0B2A4A]/10 p-5 min-w-0" data-testid={testId}>
      <h3 className="font-semibold text-[#0B2A4A] mb-3">{title}</h3>
      {rows.length === 0 ? <p className="text-sm text-slate-400">No data yet</p> : (
        <ul className="flex flex-col gap-2">
          {rows.map((r) => (
            <li key={r.name || "—"} className="relative text-sm overflow-hidden">
              <span className="absolute inset-y-0 left-0 rounded bg-[#1A9AA8]/10" style={{ width: `${(r.count / max) * 100}%` }} />
              <span className="relative flex justify-between gap-3 px-2 py-1 min-w-0"><span className="truncate min-w-0">{r.name || "—"}</span><span className="font-mono-ui text-xs text-slate-600 whitespace-nowrap shrink-0">{r.count} <span className="hidden sm:inline">{unit} · </span><span className="sm:hidden">· </span>{r.visitors} ppl</span></span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export function AnalyticsPanel() {
  const [days, setDays] = useState(7);
  const [d, setD] = useState(null);
  useEffect(() => { setD(null); api.get(`/admin/analytics?days=${days}`).then((r) => setD(r.data)).catch(() => {}); }, [days]);
  return (
    <div className="flex flex-col gap-6" data-testid="analytics-panel">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-slate-600">Visitors to your public site. Your own host visits are not counted.</p>
        <div className="flex rounded-xl border border-[#0B2A4A]/15 overflow-hidden text-sm">
          {RANGES.map(([n, l]) => <button key={n} onClick={() => setDays(n)} className={`px-3 py-1.5 font-semibold ${days === n ? "bg-[#0B2A4A] text-white" : "bg-white"}`} data-testid={`analytics-range-${n}`}>{l}</button>)}
        </div>
      </div>
      {!d ? <p className="text-sm text-slate-500">Loading analytics...</p> : (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <Kpi icon={Users} label="Visitors" value={d.totals.visitors} testId="analytics-visitors" />
            <Kpi icon={Activity} label="Visits" value={d.totals.sessions} testId="analytics-sessions" />
            <Kpi icon={Eye} label="Page views" value={d.totals.views} testId="analytics-views" />
            <Kpi icon={MousePointerClick} label="Clicks" value={d.totals.clicks} testId="analytics-clicks" />
          </div>
          <div className="bg-white rounded-2xl border border-[#0B2A4A]/10 p-5 h-72" data-testid="analytics-chart">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={d.series}>
                <CartesianGrid strokeDasharray="3 3" stroke="#0B2A4A15" />
                <XAxis dataKey="date" tick={{ fontSize: 11 }} /><YAxis allowDecimals={false} tick={{ fontSize: 11 }} /><Tooltip />
                <Area type="monotone" dataKey="views" stroke="#1A9AA8" fill="#1A9AA833" name="Page views" />
                <Area type="monotone" dataKey="visitors" stroke="#F08A3C" fill="#F08A3C33" name="Visitors" />
                <Area type="monotone" dataKey="clicks" stroke="#0B2A4A" fill="#0B2A4A11" name="Clicks" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
          <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4">
            <TopList title="Where visitors come from" rows={d.sources} testId="analytics-sources" unit="visits" />
            <TopList title="Countries" rows={d.countries} testId="analytics-countries" />
            <TopList title="Regions" rows={d.regions} testId="analytics-regions" />
            <TopList title="Cities" rows={d.cities} testId="analytics-cities" />
            <TopList title="Landing pages" rows={d.landing_pages} testId="analytics-landing" unit="visits" />
            <TopList title="Top pages" rows={d.pages} testId="analytics-pages" />
            <TopList title="Most clicked" rows={d.clicks} testId="analytics-top-clicks" unit="clicks" />
            <TopList title="Devices" rows={d.devices} testId="analytics-devices" />
            <TopList title="Browsers" rows={d.browsers} testId="analytics-browsers" />
            <TopList title="Ad / campaign links (utm_campaign)" rows={d.campaigns} testId="analytics-campaigns" unit="visits" />
          </div>
        </>
      )}
    </div>
  );
}
